import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import {
  CARD_ASPECT, add, canvasOf, faceArt, fade, fill, place, playerColor,
} from '../fx/common';
import {
  PLATE, bankFrame, formulaRow, materialCards, monsterPlate, sideRow,
} from '../fx/materialBank';

/**
 * **儀式召喚の変身バンク**（Issue #600 v1。指示書 `../direction/material_summon_ritual.md`。キービジュアルは無い＝方向は人が決めた）。
 * 疑似イベント `material_summon`（`editorPorts/materialSummon.js`）の状況 `material_summon_ritual`。**motion なし**（盤は触らない。
 * 素材はこの前の move でもう墓地、出るモンスターは後の `spsummoning` で盤に出る＝召喚バンクと 2 回あってよい）。
 * **儀式魔法のカード（`source`）が主役**（ユーザー 2026-10-02）: カードが大きく開いて扉になり、素材が吸い込まれ、扉から出るモンスターが出てくる。
 * 骨組み（並び・数式・モンスターの板・はけ）はシンクロ・融合・アドバンスと共有（`../fx/materialBank.js`）。
 *
 * ```
 * 0      溜め: 舞台を青みの暗さに。画面の中央に儀式魔法のカード（表・高さ 420）が召喚した人の側から来て立ち、
 *        その左右に素材のカード（表・下に ★n）が並ぶ
 * 380    数式 ★4 + ★4 ≥ ★8 を叩き込む（項は 380〜600、「≥ 出るモンスターのレベル」は 820。合計と同じなら「=」）。
 *        **カードは見えたまま**で、項を叩くたびに対応するカードが、右辺で儀式魔法のカードが揺れて縁が光る
 * 900    **扉が開く**: 儀式魔法のカードが高さ 700 へ大きくなりながら、左右に割れて奥へ回る（観音開き）。開いた奥は暗い空間に
 *        硬い線の放射と星のような点。扉の枠は金の細い線
 * 1120   **同時に吸い込まれる**: 素材のカードが全部同時に扉の中へ（縮みながら・傾きながら、後ろに青と白の硬い線）。着くたびに扉の縁が 1 フレーム白く
 * 1530   **閉じて叩く**: 扉が閉じかけて止まる（1600）→ 扉の形の閃光 2 フレーム・画面の閃光 1 フレーム・ヒットストップ 100ms・揺れ・隙間から鋼の破片
 * 1750   **扉から出る**: 扉が再び開き、中から出るモンスターの板（シンクロと同じ作り。影 → 色）。扉の板は左右へ倒れて消え、扉の奥も畳む。
 *        1990 名前、2070 RITUAL SUMMON の帯（縁はプレイヤーの色と青）。出るモンスターがまだ入力されていなければ扉が閉じて終わる（尺も 2300 に縮む）
 * 2800   全部が縦に細く潰れて消え、暗幕を戻す。何も残さない
 * ```
 *
 * 扉・出るモンスターは**画面の中央に固定**、絵は正立（シンクロ v3 で決まったこと）。儀式魔法が分からない（`source` が無い・絵が無い）ときは
 * 扉を地の色の鋼の板で代える。レベルが分からなければ数式は出さない。
 */
export const D = 3000;
export const D_NO_RESULT = 2300;
const ENTER = [0, 300];
const EQ = [380, 600, 820];       // 数式: 最初の項 / 最後の項 / 「≥ 右辺」
const GROW = [900, 1000];          // 扉が大きくなる
const OPEN = [940, 1100];          // 扉が開く（奥へ回る）
const SUCK = [1120, 1560];         // 素材が吸い込まれる（最後のカードが着くまで）
const FLY = 360;                   // カード 1 枚が扉へ着くまで
const CLOSE = [1530, 1600];        // 閉じかける
const SLAM = 1600;
const STOP = 100;
const SHUT = [SLAM + STOP, SLAM + STOP + 60];   // 出るモンスターが無いとき: 閉じきる
const REOPEN = [1750, 1830];
const FALL = [1830, 2050];         // 扉の板が左右へ倒れて消える
const VOID_FOLD = [1900, 1990];    // 扉の奥を畳む（出るモンスターの板の後ろ）
const BURST = [1760, 1960];        // 扉の縁から外へ放射の硬い線
const MON = 1780;
const MON_OPEN = 140;
const MON_COLOR = [1840, 1990];
const NAME_HIT = 1990;
const BAND_HIT = 2070;

const OPEN_A = 1.22;               // 開いた扉の角度（ラジアン）
const AJAR_A = 0.16;               // 閉じかけて止まる角度
const REOPEN_A = 1.4;
const RITUAL_BLUE = '#3d8bff';
const PALE_BLUE = '#9cc4ff';
const GOLD = '#d9b25a';
const DOOR_H0 = 420;
const DOOR_H1 = 700;
const DOOR_Y0 = 600;
const DOOR_Y1 = PLATE.CY;          // 出るモンスターの板と同じ高さ
const HOLE = 250;
const RAYS = 22;
const STARS = 42;
const BURST_RAYS = 18;
const SHARDS = 16;
const TRAIL_PTS = 6;
const FORMULA_Y = 290;             // 紋章の下のフェイズ名に掛からず、扉の上端より上

/** 止めの時間の写し（閉じて叩いた止めの間は奥の線・星・破片が止まる） */
const frozen = (t) => {
  if (t < SLAM) return t;
  if (t < SLAM + STOP) return SLAM;
  return t - STOP;
};

const lvOf = (m) => (Number.isFinite(m?.level) && m.level > 0 ? m.level : null);

/** 帯の尺（出るモンスターが無ければ扉が閉じて終わる） */
export const durationOf = (payload) => (payload?.result?.code != null ? D : D_NO_RESULT);

/** 数式の項を叩き込む時刻（最初の項は 380、残りの項は 600 までに等分。右辺は `EQ[2]`） */
export function formulaTimes(n) {
  if (n <= 1) return [EQ[0]];
  return Array.from({ length: n }, (_, i) => EQ[0] + ((EQ[1] - EQ[0]) * i) / (n - 1));
}
export const RHS_AT = EQ[2];
export const OPEN_AT = OPEN[0];
export const SLAM_AT = SLAM;

/**
 * **数式**（純関数）: 素材のレベルが全部分かれば `{ terms, op, rhs }`。右辺は出るモンスターのレベル（分かれば）で、
 * 合計と同じなら「=」、合計のほうが大きければ「≥」。出るモンスターのレベルが分からなければ「= 合計」。素材のレベルが 1 つでも分からなければ null
 */
export function ritualFormula(payload) {
  const mats = Array.isArray(payload?.materials) ? payload.materials : [];
  const terms = mats.map(lvOf);
  if (!terms.length || terms.some((l) => l == null)) return null;
  const total = terms.reduce((a, b) => a + b, 0);
  const rl = lvOf(payload?.result);
  if (rl == null) return { terms, op: '=', rhs: total };
  return { terms, op: total === rl ? '=' : '≥', rhs: rl };
}

/** 素材のカード i が扉へ飛び始める時刻（全部ほぼ同時。最後のカードが `SUCK[1]` に着く） */
export function suckStart(i, n) {
  const g = n > 1 ? Math.min(60, (SUCK[1] - SUCK[0] - FLY) / (n - 1)) : 0;
  return SUCK[0] + i * g;
}

function sceneFor(payload) {
  const f = ritualFormula(payload);
  const out = [
    'reset',
    {
      ms: 0,
      grade: {
        contrast: {
          keys: [
            { ms: SLAM - 1, value: 1 }, { ms: SLAM, value: 1.45 }, { ms: SLAM + STOP, value: 1.45, ease: 'hold' }, { ms: SLAM + STOP + 1, value: 1 },
          ],
        },
        chroma: { keys: [{ ms: SLAM - 1, value: 0 }, { ms: SLAM, value: 0.016 }, { ms: SLAM + 34, value: 0, ease: 'ease_out' }] },
      },
    },
    { ms: OPEN[0] + 20, shake: { amp: 12, freq: 18, decayMs: 260 } },
    { ms: SLAM + STOP, shake: { amp: 36, freq: 22, decayMs: 420 } },
  ];
  if (f) {
    const slams = [...new Set(formulaTimes(f.terms.length).map(Math.round)), EQ[2]];
    slams.forEach((t, i) => out.push({ ms: t + 10, shake: { amp: i === slams.length - 1 ? 16 : 9, freq: 28, decayMs: 160 } }));
  }
  if (payload?.result?.code != null) {
    out.push({ ms: REOPEN[0], shake: { amp: 14, freq: 20, decayMs: 240 } });
    out.push({ ms: NAME_HIT, shake: { amp: 8, freq: 30, decayMs: 160 } });
    out.push({ ms: BAND_HIT, shake: { amp: 14, freq: 26, decayMs: 220 } });
  }
  return out;
}

export default defineEffect({
  situation: 'material_summon_ritual',
  role: 'effect',
  label: '儀式召喚の変身バンク（儀式魔法のカードが扉になる → 素材が吸い込まれる → 扉から出るモンスター）',
  color: RITUAL_BLUE,
  duration: ({ payload } = {}) => durationOf(payload),
  scene: (ctx) => sceneFor(ctx?.payload),
  sounds: ({ payload } = {}) => {
    const f = ritualFormula(payload);
    const out = [{ at: 0, name: 'phase_change', gain: 0.55 }];
    if (f) {
      formulaTimes(f.terms.length).forEach((t) => out.push({ at: Math.round(t) - 10, name: 'set_card', gain: 0.7 }));
      out.push({ at: EQ[2] - 10, name: 'set_card', gain: 0.9 });
    }
    out.push({ at: OPEN[0] - 10, name: 'effect', gain: 0.85 });
    out.push({ at: SUCK[0] + 40, name: 'draw', gain: 0.5 });
    out.push({ at: SUCK[0] + 260, name: 'draw', gain: 0.5 });
    out.push({ at: SLAM - 15, name: 'attack', gain: 1 });
    if (payload?.result?.code != null) out.push({ at: REOPEN[0], name: 'summon' });
    return out;
  },

  layers: [{
    id: 'bank',
    label: '儀式魔法の扉 → 素材が吸い込まれる → 扉から出るモンスター',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const { w: W, h: H } = canvasOf(ctx);
      const { payload } = ctx;
      const p = payload.player === 1 ? 1 : 0;
      const pc = playerColor(ctx, p);
      const materials = Array.isArray(payload.materials) ? payload.materials : [];
      const result = payload.result?.code != null ? payload.result : null;
      const dur = durationOf(payload);
      const rand = seeded(`${ctx.seed}:ritual`);
      const frame = bankFrame(ctx, { dur, dim: '#03060f', dimAlpha: 0.6 });
      const { layer, metal } = frame;
      const CX = W / 2;

      // --- 素材のカード（扉の左右の外側。半分ずつ左右へ。下に ★n）
      const n = materials.length;
      const CW = Math.min(230, 1400 / Math.max(1, n) - 60);
      const ROW_Y = DOOR_Y0;
      const nL = Math.floor(n / 2) + (n % 2);
      const lay = sideRow(nL, n - nL, CW, { W, hole: HOLE });
      const formula = ritualFormula(payload);
      const sumTimes = formula ? formulaTimes(formula.terms.length) : [];
      const row = materialCards(ctx, frame, {
        materials, player: p, xs: lay.xs, y: ROW_Y, cw: CW, rand, hitsOf: (i) => (formula ? [sumTimes[i], EQ[2]] : []),
      });
      const CH = row.ch;
      const cards = row.items.map((mc) => {
        const lv = lvOf(mc.m);
        const label = lv != null ? metal(`★${lv}`, 60) : null;
        if (label) add(layer(6), label.m, 6);
        const color = mc.x < CX ? RITUAL_BLUE : PALE_BLUE;
        const trail = Array.from({ length: TRAIL_PTS - 1 }, () => ({
          m: add(layer(3.8), fill(ctx, color, 1, 1), 3.8), w: add(layer(3.9), fill(ctx, '#ffffff', 1, 1), 3.9),
        }));
        const start = suckStart(mc.i, n);
        return Object.assign(mc, {
          label, trail, start, end: start + FLY, side: mc.x < CX ? -1 : 1, jx: (rand() - 0.5) * 40, jy: (rand() - 0.5) * 30,
        });
      });

      // --- 儀式魔法のカード＝扉（1 枚の絵を左右の 2 枚に割る。蝶番は外側の縦の辺）
      const DW1 = DOOR_H1 * CARD_ASPECT;
      const img = faceArt(ctx, { code: payload.source?.code ?? null, player: p });
      const tex = kit.imageTexture(img ?? null);
      const doorMat = tex
        ? kit.cardArtMaterial(tex, { brightness: 1.12, saturation: 1.3 })
        : kit.plain(kit.steelPlateTexture(Math.round(DW1), DOOR_H1, { cut: 44, accent: RITUAL_BLUE }));
      const leafL = add(layer(3), kit.hingeLeaf(doorMat, DW1 / 2, DOOR_H1, { side: 'left' }), 3);
      const leafR = add(layer(3), kit.hingeLeaf(doorMat, DW1 / 2, DOOR_H1, { side: 'right' }), 3);
      // 扉の奥（暗い空間・硬い線の放射・星のような点）
      const voidBg = add(layer(2), fill(ctx, '#02040b', DW1, DOOR_H1), 2);
      const rays = Array.from({ length: RAYS }, (_, k) => ({
        m: add(layer(2.2), fill(ctx, k % 3 === 0 ? '#ffffff' : PALE_BLUE, 1, 1), 2.2),
        a: (k / RAYS) * Math.PI * 2 + (rand() - 0.5) * 0.18, r0: 24 + rand() * 50, reach: 0.55 + rand() * 0.45, w: 2 + rand() * 3, ph: rand() * 10,
      }));
      const stars = Array.from({ length: STARS }, () => ({
        m: add(layer(2.3), fill(ctx, rand() < 0.3 ? PALE_BLUE : '#ffffff', 1, 1), 2.3),
        u: (rand() - 0.5) * 0.9, v: (rand() - 0.5) * 0.92, size: 4 + rand() * 6, ph: rand(), tw: Math.floor(rand() * 4),
      }));
      // 扉の枠（金の細い線。開いてから）と、縁の白い閃光（叩いた・着いた瞬間）
      const rim = [0, 1, 2, 3].map(() => add(layer(3.5), fill(ctx, GOLD, 1, 1), 3.5));
      const rimOut = [0, 1, 2, 3].map(() => add(layer(3.4), fill(ctx, '#050607', 1, 1), 3.4));
      const rimLit = [0, 1, 2, 3].map(() => add(layer(3.6), fill(ctx, '#ffffff', 1, 1), 3.6));
      const slamFlash = add(layer(9.5), fill(ctx, '#ffffff', DW1, DOOR_H1), 9.5);
      const flash = add(layer(14), place(fill(ctx, '#ffffff', W, H), W / 2, H / 2), 14);
      const slit = add(layer(3.7), fill(ctx, '#ffffff', 1, 1), 3.7);
      const shards = Array.from({ length: SHARDS }, (_, k) => {
        const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
        m.material.color?.set?.(k % 3 === 0 ? '#e6e9ee' : (k % 3 === 1 ? PALE_BLUE : '#9aa1ad'));
        add(layer(8), m, 8);
        return {
          m, side: k % 2 === 0 ? -1 : 1, y: (rand() - 0.5) * DOOR_H1 * 0.8, a: (rand() - 0.5) * 1.2, size: 10 + rand() * 16,
          stretch: 1.5 + rand() * 1.5, speed: 500 + rand() * 900, spin: (rand() - 0.5) * 26,
        };
      });
      const burstRays = Array.from({ length: BURST_RAYS }, (_, k) => ({
        m: add(layer(11), fill(ctx, k % 3 === 0 ? RITUAL_BLUE : '#ffffff', 1, 1), 11),
        a: (k / BURST_RAYS) * Math.PI * 2 + (rand() - 0.5) * 0.2, len: 220 + rand() * 260, w: 4 + rand() * 7,
      }));

      // --- 数式（★4 + ★4 ≥ ★8。右辺だけ大きく金）
      const eqTokens = [];
      if (formula) {
        formula.terms.forEach((lv, i) => {
          if (i > 0) eqTokens.push({ text: '+', size: 96, at: sumTimes[i] });
          eqTokens.push({ text: `★${lv}`, size: 104, at: sumTimes[i] });
        });
        eqTokens.push({ text: formula.op, size: 96, at: EQ[2] });
        eqTokens.push({ text: `★${formula.rhs}`, size: 150, at: EQ[2], gold: true });
      }
      const eq = formulaRow(ctx, frame, eqTokens, { maxW: 1300 });

      // --- 出るモンスター（扉の所＝画面の中央）
      const mon = result ? monsterPlate(ctx, frame, {
        result, player: p, pc, accent: '#1d4f9a', bandColor: RITUAL_BLUE, tag: 'RITUAL SUMMON', cx: CX,
      }) : null;

      /** 扉の角度（0 = 閉じて平ら） */
      const angleAt = (t) => {
        if (t < OPEN[0]) return 0;
        if (t < CLOSE[0]) return OPEN_A * tween(t, OPEN[0], OPEN[1], 'power3_out');
        if (t < SLAM) return OPEN_A + (AJAR_A - OPEN_A) * tween(t, CLOSE[0], CLOSE[1], 'power3_in');
        if (t < SLAM + STOP) return AJAR_A;
        if (!result) return AJAR_A * (1 - tween(t, SHUT[0], SHUT[1], 'power2_in'));
        if (t < REOPEN[0]) return AJAR_A;
        return AJAR_A + (REOPEN_A - AJAR_A) * tween(t, REOPEN[0], REOPEN[1], 'expo_out');
      };

      return {
        root: frame.top,
        update: ({ localMs: t }) => {
          const v = frozen(t);
          frame.update(t);

          // --- 扉: 召喚した人の側から来て立つ → 大きくなりながら開く → 閉じかけて叩く → 再び開いて板が倒れる
          const ek = tween(t, ENTER[0], ENTER[1], 'power3_out');
          const gk = tween(t, GROW[0], GROW[1], 'expo_out');
          const scale = (DOOR_H0 + (DOOR_H1 - DOOR_H0) * gk) / DOOR_H1;
          const fromY = p === 1 ? -DOOR_H0 : H + DOOR_H0;
          const restY = DOOR_Y0 + (DOOR_Y1 - DOOR_Y0) * gk;
          const hitK = t >= EQ[2] && t < EQ[2] + 180 ? 1 - seg(t, EQ[2], EQ[2] + 180) : 0;
          const quiver = formula ? Math.sin((t - EQ[2]) / 9) * 7 * hitK : 0;
          const bump = formula && t >= EQ[2] ? 1 + 0.04 * Math.sin(seg(t, EQ[2], EQ[2] + 140) * Math.PI) : 1;
          const dx = CX + quiver;
          const dy = fromY + (restY - fromY) * ek;
          const drot = (1 - ek) * (p === 1 ? -0.3 : 0.3);
          const s = scale * bump;
          const hw = (DW1 / 2) * s; const hh = (DOOR_H1 / 2) * s;
          const ang = angleAt(t);
          const fall = result ? tween(t, FALL[0], FALL[1], 'power2_in') : 0;
          [[leafL, -1], [leafR, 1]].forEach(([leaf, side]) => {
            kit.swingLeaf(leaf, ang);
            const hx = dx + side * hw * Math.cos(drot);
            const hy = dy + side * hw * Math.sin(drot);
            place(leaf, hx + side * 260 * fall, hy + 380 * fall * fall, { s, rot: drot + side * 1.1 * fall });
            fade(leaf, fall < 1 ? 1 - fall * fall : 0);
          });

          // 扉の奥（開いている間だけ。出るモンスターの後ろで横に畳む）
          const fold = result ? 1 - tween(t, VOID_FOLD[0], VOID_FOLD[1], 'power2_in') : 1;
          const open = ang > 0.02 && fold > 0.001;
          const vw = hw * fold;
          place(voidBg, dx, dy, { sx: s * fold, sy: s });
          fade(voidBg, open ? 1 : 0);
          const step = Math.floor(v / 50);
          rays.forEach((r) => {
            const a = r.a + v * 0.00025;
            const ca = Math.abs(Math.cos(a)); const sa = Math.abs(Math.sin(a));
            const edge = Math.min(ca > 1e-4 ? vw / ca : Infinity, sa > 1e-4 ? hh / sa : Infinity);
            const jit = 0.8 + 0.2 * Math.abs(Math.sin(r.ph + step * 1.3));
            const r0 = r.r0 * s; const r1 = Math.max(r0, edge * r.reach * jit - 4);
            const mid = (r0 + r1) / 2;
            place(r.m, dx + Math.cos(a) * mid, dy + Math.sin(a) * mid, { sx: Math.max(0.001, r1 - r0), sy: r.w, rot: a });
            fade(r.m, open && r1 - r0 > 4 ? 0.75 : 0);
          });
          stars.forEach((st) => {
            // 奥から手前へ流れる（中心から縁へ広がって大きくなり、縁に着いたら中心へ戻る）
            const f = (v / 1400 + st.ph) % 1;
            const k = 0.25 + 0.75 * f;
            const sz = st.size * (0.6 + 0.7 * f) * s;
            place(st.m, dx + st.u * 2 * vw * k, dy + st.v * 2 * hh * k, { sx: sz, sy: sz, rot: Math.PI / 4 });
            fade(st.m, open && (step + st.tw) % 4 !== 0 ? 1 : 0);
          });

          // 扉の枠（金の細い線）と縁の白（右辺を叩いた・カードが着いた・叩いて閉じた瞬間）
          const arrived = cards.some((c) => t >= c.end && t < c.end + 34);
          const lit = (formula && t >= EQ[2] && t < EQ[2] + 34) || arrived || (t >= SLAM && t < SLAM + 67);
          const rimOn = t >= OPEN[0] && fold > 0.001;
          const bars = [[0, -hh, 2 * vw, 0], [vw, 0, 0, 2 * hh], [0, hh, 2 * vw, 0], [-vw, 0, 0, 2 * hh]];
          bars.forEach(([ox, oy, bw, bh], j) => {
            place(rimOut[j], dx + ox, dy + oy, { sx: bw + 14, sy: bh + 14 });
            place(rim[j], dx + ox, dy + oy, { sx: bw + 6, sy: bh + 6 });
            place(rimLit[j], dx + ox, dy + oy, { sx: bw + 10, sy: bh + 10 });
            fade(rimOut[j], rimOn ? 1 : 0);
            fade(rim[j], rimOn ? 1 : 0);
            fade(rimLit[j], lit && (rimOn || t < OPEN[0]) ? 1 : 0);
          });

          // 叩いて閉じた: 扉の形の閃光 2 フレーム・画面の閃光 1 フレーム・隙間の白い線・隙間から鋼の破片
          place(slamFlash, dx, dy, { s });
          fade(slamFlash, t >= SLAM && t < SLAM + 67 ? 0.95 : 0);
          fade(flash, t >= SLAM && t < SLAM + 34 ? 0.5 : 0);
          const gap = 2 * hw * (1 - Math.cos(ang));
          place(slit, dx, dy, { sx: Math.max(2, Math.min(gap, 18)), sy: 2 * hh * 0.96 });
          fade(slit, t >= SLAM && t < (result ? REOPEN[0] : SHUT[1]) ? 1 : 0);
          const since = Math.max(0, (v - SLAM) / 1000);
          shards.forEach((sh) => {
            const k = Math.min(1, since / 0.55);
            const r = sh.speed * since;
            const a = sh.side < 0 ? Math.PI + sh.a : sh.a;
            sh.m.position.set(dx + Math.cos(a) * r, dy + sh.y + Math.sin(a) * r + 1300 * since * since, 0);
            sh.m.rotation.z = a + sh.spin * since;
            sh.m.scale.set(sh.size * sh.stretch, sh.size, 1);
            fade(sh.m, t >= SLAM && k < 1 ? 1 - k * k : 0);
          });

          // 扉から出る: 縁から外へ放射の硬い線
          const bk = seg(t, BURST[0], BURST[1]);
          burstRays.forEach((r) => {
            const d0 = Math.min(hw, hh) + 40 + 380 * tween(t, BURST[0], BURST[1], 'expo_out');
            const len = r.len * (1 - bk);
            const mid = d0 + len / 2;
            place(r.m, dx + Math.cos(r.a) * mid, dy + Math.sin(r.a) * mid, { sx: Math.max(0.001, len), sy: Math.max(0.001, r.w * (1 - bk)), rot: r.a });
            fade(r.m, result && t >= BURST[0] && bk < 1 ? 1 : 0);
          });

          // --- 素材のカード: 並ぶ → 項を叩くたびに揺れ・縁 → 扉へ同時に吸い込まれる（縮みながら・傾きながら・後ろに硬い線）
          const tx = CX; const ty = DOOR_Y1;
          const pathAt = (c, tt) => {
            const k = tween(tt, c.start, c.end, 'power2_in');
            const ctrlX = (c.x + tx) / 2; const ctrlY = Math.min(c.y, ty) - 140;
            const ex = tx + c.jx; const ey = ty + c.jy;
            return {
              x: (1 - k) * (1 - k) * c.x + 2 * (1 - k) * k * ctrlX + k * k * ex,
              y: (1 - k) * (1 - k) * c.y + 2 * (1 - k) * k * ctrlY + k * k * ey,
              s: 1 - 0.9 * k,
              rot: -c.side * 0.8 * k,
            };
          };
          cards.forEach((c) => {
            const pose = t < c.start ? row.pose(c, t) : pathAt(c, t);
            place(c.c.mesh, pose.x, pose.y, { s: pose.s, rot: pose.rot });
            fade(c.c.mesh, t < c.end ? 1 : 0);
            row.edges(c, pose.x, pose.y, pose.s, Boolean(pose.lit) && t < c.start, pose.rot);
            fade(c.flash, 0);
            if (c.label) {
              place(c.label.m, c.x, c.y + CH / 2 + 48);
              fade(c.label.m, seg(t, 150, 250) * (t < c.start ? 1 : 0));
            }
            const pts = Array.from({ length: TRAIL_PTS }, (_, j) => pathAt(c, Math.max(c.start, t - j * 30)));
            c.trail.forEach((sg, j) => {
              const a = pts[j]; const b = pts[j + 1];
              const ddx = a.x - b.x; const ddy = a.y - b.y;
              const len = Math.hypot(ddx, ddy);
              const wdt = 30 * (1 - j / TRAIL_PTS) * Math.max(0.4, a.s);
              const on = t >= c.start + 20 && t < c.end && len > 2;
              const rot = Math.atan2(ddy, ddx);
              place(sg.m, (a.x + b.x) / 2, (a.y + b.y) / 2, { sx: Math.max(0.001, len + wdt * 0.5), sy: Math.max(0.001, wdt), rot });
              place(sg.w, (a.x + b.x) / 2, (a.y + b.y) / 2, { sx: Math.max(0.001, len), sy: Math.max(0.001, wdt * 0.3), rot });
              fade(sg.m, on ? 1 : 0);
              fade(sg.w, on ? 1 : 0);
            });
          });

          // --- 数式: 叩き込む → 扉が開いたら扉へ吸われて消える
          const suck = tween(t, OPEN[0], OPEN[0] + 160, 'power2_in');
          eq.update(t, {
            y: FORMULA_Y + (ty - FORMULA_Y) * suck, toX: tx, suck, hideAt: OPEN[0] + 160,
          });

          // --- 出るモンスター
          mon?.update(t, {
            at: MON, open: MON_OPEN, colorAt: MON_COLOR, nameAt: NAME_HIT, bandAt: BAND_HIT,
          });
        },
      };
    },
  }],
}, 'material_summon_ritual_effect.js');
